import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin, Clock } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { useReserve } from "../contexts/ReserveContext";
import { PHONE, PHONE_TEL, ADDRESS, MAPS_EMBED_URL, MAPS_LINK, ASSETS } from "../data/constants";

// Hero slideshow — authentic AMOS imagery.
const HERO_SLIDES = [
    ASSETS.venueEntrance,
    ASSETS.dessertCushion,
    ASSETS.miniBurgers,
    ASSETS.steakPlate,
];

const HomePage = () => {
    const { t } = useLang();
    const { open: openReserve } = useReserve();
    const [slide, setSlide] = useState(0);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const id = setInterval(() => {
            setSlide((s) => (s + 1) % HERO_SLIDES.length);
        }, 6000);
        return () => clearInterval(id);
    }, []);

    return (
        <div data-testid="home-page">
            {/* ===================== HERO — sliding slideshow ===================== */}
            <section className="relative h-[100vh] min-h-[640px] w-full overflow-hidden">
                {/* Sliding track */}
                <div
                    data-testid="hero-track"
                    className="absolute inset-0 flex h-full will-change-transform"
                    style={{
                        width: `${HERO_SLIDES.length * 100}%`,
                        transform: `translate3d(-${slide * (100 / HERO_SLIDES.length)}%, 0, 0)`,
                        transition: "transform 1100ms cubic-bezier(0.65, 0, 0.35, 1)",
                    }}
                >
                    {HERO_SLIDES.map((src, i) => (
                        <div
                            key={src}
                            data-testid={`hero-slide-${i}`}
                            className="h-full bg-cover bg-center shrink-0"
                            style={{
                                width: `${100 / HERO_SLIDES.length}%`,
                                backgroundImage: `url(${src})`,
                            }}
                        />
                    ))}
                </div>

                {/* Overlay */}
                <div className="absolute inset-0 hero-overlay" />

                {/* Content — centered editorial */}
                <div className="relative z-10 h-full container-x flex flex-col justify-center items-center text-center pt-20">
                    <div className="max-w-4xl">
                        <p
                            data-testid="hero-eyebrow"
                            className="font-display uppercase tracking-[0.5em] text-[10px] md:text-xs text-white/85 mb-8 animate-fade-in"
                        >
                            {t("hero.eyebrow")}
                        </p>
                        <h1
                            data-testid="hero-title"
                            className="font-accent italic font-light text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[120px] leading-[1.05] animate-fade-up"
                        >
                            {t("hero.title1")}
                        </h1>
                        <div
                            className="mt-12 flex flex-wrap justify-center gap-4 animate-fade-up"
                            style={{ animationDelay: "0.4s" }}
                        >
                            <button
                                type="button"
                                onClick={openReserve}
                                data-testid="hero-cta-reserve"
                                className="btn-primary"
                            >
                                {t("hero.ctaReserve")} <ArrowRight size={16} />
                            </button>
                            <Link to="/menu" data-testid="hero-cta-menu" className="btn-ghost">
                                {t("hero.ctaMenu")}
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Slide indicators */}
                <div
                    data-testid="hero-indicators"
                    className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex gap-2"
                >
                    {HERO_SLIDES.map((_, i) => (
                        <button
                            key={i}
                            data-testid={`hero-indicator-${i}`}
                            onClick={() => setSlide(i)}
                            aria-label={`Slide ${i + 1}`}
                            className={`h-[2px] rounded-full transition-all duration-500 ${
                                slide === i ? "w-12 bg-white" : "w-6 bg-white/35 hover:bg-white/60"
                            }`}
                        />
                    ))}
                </div>
            </section>

            {/* ===================== ABOUT — quietly luxurious, with image ===================== */}
            <section data-testid="about-section" className="py-28 md:py-36 bg-ivory relative overflow-hidden">
                {/* Soft watermark wave */}
                <svg aria-hidden="true" className="absolute -right-32 top-1/2 -translate-y-1/2 w-[600px] opacity-[0.06]" viewBox="0 0 200 200" fill="none">
                    <circle cx="100" cy="100" r="80" stroke="#1E8FA5" strokeWidth="0.5" />
                    <circle cx="100" cy="100" r="60" stroke="#1E8FA5" strokeWidth="0.5" />
                    <circle cx="100" cy="100" r="40" stroke="#1E8FA5" strokeWidth="0.5" />
                </svg>
                <div className="container-x grid lg:grid-cols-2 gap-12 lg:gap-20 items-center relative">
                    <div className="order-2 lg:order-1">
                        <p className="eyebrow mb-6">{t("about.eyebrow")}</p>
                        <h2 className="font-accent italic font-light text-aegean-800 text-4xl md:text-5xl lg:text-6xl leading-[1.1]">
                            {t("about.title")}
                        </h2>
                        <div className="mt-8 h-px w-16 bg-aegean-300" />
                        <p className="mt-8 text-ink/70 text-base md:text-lg leading-[1.9] font-light max-w-xl">
                            {t("about.body")}
                        </p>
                    </div>
                    <div className="order-1 lg:order-2 relative">
                        <div className="aspect-[4/5] overflow-hidden shadow-[0_30px_80px_-30px_rgba(11,53,64,0.35)]">
                            <img src={ASSETS.venueEntrance} alt="AMOS seaside entrance" className="w-full h-full object-cover" />
                        </div>
                        <div className="hidden md:block absolute -bottom-10 -left-10 w-48 h-60 overflow-hidden border-8 border-ivory shadow-2xl">
                            <img src={ASSETS.dessertCushion} alt="Signature dessert" className="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* ===================== EDITORIAL — light blue band ===================== */}
            <section className="py-28 md:py-36 bg-aegean-50 relative overflow-hidden">
                {/* subtle dotted pattern */}
                <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
                     style={{
                         backgroundImage: "radial-gradient(circle at 1px 1px, #1E8FA5 1px, transparent 0)",
                         backgroundSize: "32px 32px",
                     }}
                />
                <div className="container-x grid lg:grid-cols-2 gap-12 lg:gap-20 items-center relative">
                    <div className="aspect-[5/4] overflow-hidden shadow-2xl">
                        <img src={ASSETS.steakPlate} alt="Steak from the AMOS kitchen" className="w-full h-full object-cover" />
                    </div>
                    <div>
                        <p className="eyebrow mb-6">{t("editorial.eyebrow")}</p>
                        <h2 className="font-accent italic font-light text-aegean-800 text-4xl md:text-5xl lg:text-6xl leading-[1.1]">
                            {t("editorial.title")}
                        </h2>
                        <div className="mt-8 h-px w-16 bg-aegean-400" />
                        <p className="mt-8 text-ink/70 text-base md:text-lg leading-[1.9] font-light max-w-md">
                            {t("editorial.body")}
                        </p>
                        <Link to="/menu" data-testid="editorial-cta" className="mt-10 btn-outline-dark inline-flex">
                            {t("editorial.cta")} <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ===================== FULL-BLEED EDITORIAL IMAGE ===================== */}
            <section className="relative h-[70vh] min-h-[480px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${ASSETS.dessertCushion})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-aegean-900/65 via-aegean-900/30 to-transparent" />
                <div className="relative z-10 h-full container-x flex items-end pb-16 md:pb-20">
                    <div className="max-w-2xl text-white">
                        <p className="font-display uppercase tracking-[0.4em] text-[10px] md:text-xs text-white/80 mb-5">
                            {t("fullBleed.eyebrow")}
                        </p>
                        <h2 className="font-accent italic font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                            {t("fullBleed.title")}
                        </h2>
                        <p className="mt-6 max-w-xl text-white/85 text-base md:text-lg leading-[1.85] font-light">
                            {t("fullBleed.body")}
                        </p>
                    </div>
                </div>
            </section>

            {/* ===================== LOCATION PREVIEW ===================== */}
            <section data-testid="location-preview-section" className="py-32 md:py-40 bg-ivory">
                <div className="container-x">
                    <div className="max-w-xl mx-auto text-center mb-16">
                        <p className="eyebrow mb-7">{t("location.eyebrow")}</p>
                        <h2 className="font-accent italic font-light text-aegean-800 text-4xl md:text-5xl lg:text-6xl leading-[1.1]">
                            {t("location.title")}
                        </h2>
                        <div className="mt-10 mx-auto h-px w-16 bg-aegean-300" />
                    </div>

                    <div className="grid lg:grid-cols-5 gap-6">
                        <div className="lg:col-span-3 aspect-[16/10] overflow-hidden shadow-2xl">
                            <iframe
                                title="AMOS location"
                                src={MAPS_EMBED_URL}
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                        <div className="lg:col-span-2 bg-white p-10 md:p-12 flex flex-col justify-between shadow-2xl">
                            <div className="space-y-7">
                                <div className="flex items-start gap-3">
                                    <MapPin size={18} className="text-aegean-500 mt-1 shrink-0" />
                                    <div>
                                        <div className="font-display uppercase tracking-[0.25em] text-[10px] text-aegean-600 mb-1.5">
                                            Address
                                        </div>
                                        <div className="text-ink font-light">
                                            {ADDRESS}
                                            <br />
                                            Lemnos, Greece
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <Phone size={18} className="text-aegean-500 mt-1 shrink-0" />
                                    <div>
                                        <div className="font-display uppercase tracking-[0.25em] text-[10px] text-aegean-600 mb-1.5">
                                            Phone
                                        </div>
                                        <a
                                            href={`tel:${PHONE_TEL}`}
                                            className="text-ink font-light hover:text-aegean-500"
                                        >
                                            {PHONE}
                                        </a>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <Clock size={18} className="text-aegean-500 mt-1 shrink-0" />
                                    <div>
                                        <div className="font-display uppercase tracking-[0.25em] text-[10px] text-aegean-600 mb-1.5">
                                            Hours
                                        </div>
                                        <div className="text-ink font-light">{t("location.hours")}</div>
                                    </div>
                                </div>
                            </div>
                            <a
                                href={MAPS_LINK}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="home-maps-cta"
                                className="btn-primary mt-10 w-full"
                            >
                                {t("location.cta")} <ArrowRight size={16} />
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===================== FINAL CTA — dark aegean band ===================== */}
            <section className="py-28 md:py-36 bg-aegean-700 text-white relative overflow-hidden">
                {/* Decorative wave */}
                <svg aria-hidden="true" className="absolute inset-x-0 -top-1 w-full opacity-10" viewBox="0 0 1440 100" fill="none">
                    <path d="M0,50 Q360,0 720,50 T1440,50" stroke="white" strokeWidth="1" fill="none" />
                    <path d="M0,80 Q360,30 720,80 T1440,80" stroke="white" strokeWidth="1" fill="none" />
                </svg>
                <div className="container-x text-center max-w-xl mx-auto relative">
                    <p className="font-display uppercase tracking-[0.4em] text-[10px] md:text-xs text-aegean-200 mb-7">
                        {t("finalCta.eyebrow")}
                    </p>
                    <h2 className="font-accent italic font-light text-white text-4xl md:text-5xl lg:text-6xl leading-[1.1]">
                        {t("finalCta.title")}
                    </h2>
                    <div className="mt-10 mx-auto h-px w-16 bg-aegean-300" />
                    <p className="mt-10 text-white/75 text-base md:text-lg leading-[1.9] font-light">
                        {t("finalCta.body")}
                    </p>
                    <div className="mt-12 flex flex-wrap justify-center gap-4">
                        <button type="button" onClick={openReserve} data-testid="final-cta-reserve" className="btn-pill bg-white text-aegean-700 hover:bg-aegean-300 hover:text-white">
                            {t("nav.reserveTable")} <ArrowRight size={16} />
                        </button>
                        <a
                            href={`tel:${PHONE_TEL}`}
                            data-testid="final-cta-call"
                            className="btn-ghost"
                        >
                            <Phone size={14} /> {PHONE}
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
